import { memo, useState } from "react";
import StatCardRow from "../../shared/StatCardRow";
import RecordModal from "../../shared/RecordModal";
import DashboardTable from "../../shared/DashboardTable";
import TableLoading from "../../shared/TableLoading";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useMountFlash } from "../../hooks/useMountFlash";
import {
  fetchCases,
  fetchProjects,
  fetchVehicles,
  fetchEquipment,
  fetchCustomAssets,
} from "../../services/dashboardApi";
import { withId } from "../../utils/drillHelpers";
import { formatDate } from "../../utils/formatters";
import { toPlainText } from "../../utils/plainText";
import "../../reportDashboard.scss";

// Corrective-rate bands shared with the Inspections section's day-of-week/monthly tone helpers.
const rateTone = (rate) =>
  rate > 12 ? "danger" : rate > 8 ? "warning" : "kudos";

// Linked-cases search (openProjectDetail, below) runs up to two independent
// `search` queries (by project name, by location) and merges + dedupes their
// results, so a single server page from each isn't a meaningful "the results"
// the way it is everywhere else in this feature — the true match set can
// only be known by walking every page of each search. fetchAllPages does
// that: it keeps requesting pageSize-sized pages from `fetchFn` until either
// every row the API reports via totalCount has been collected, or `maxRows`
// is hit, whichever comes first. `maxRows` exists so a pathological search
// term (e.g. a location string shared by thousands of cases) can't turn one
// click into an unbounded number of requests — see the `truncated` flag
// below, which callers must surface visibly rather than silently handing
// back a partial list that looks complete.
async function fetchAllPages(fetchFn, params, { pageSize = 100, maxRows = 1000 } = {}) {
  let rows = [];
  let page = 1;
  let totalCount = null;
  for (;;) {
    // Pages must be walked in order — each request needs the running rows
    // count to decide whether another page is worth asking for — so this
    // can't be parallelized the way the two independent searches themselves
    // are (via Promise.all in openProjectDetail).
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchFn({ ...params, page, pageSize });
    const pageRows = result?.rows || [];
    rows = rows.concat(pageRows);
    totalCount = result?.totalCount ?? rows.length;
    // pageRows.length === 0 guards against a stalled/incorrect totalCount
    // (e.g. an API bug reporting more rows than it can actually return)
    // turning this into an infinite loop.
    if (pageRows.length === 0 || rows.length >= totalCount || rows.length >= maxRows) {
      break;
    }
    page += 1;
  }
  return { rows: rows.slice(0, maxRows), truncated: rows.length < totalCount };
}

const centeredCount = { type: "number", align: "center", headerAlign: "center" };

// Client-side page size for the Linked Cases cards list (see fetchAllPages /
// openProjectDetail below) — the same 10-per-page default usePaginatedList
// seeds everywhere else, kept as a plain constant here since this list isn't
// server-paged.
const LINKED_CASES_PAGE_SIZE = 10;

const projectColumns = [
  { field: "projectName", headerName: "Project", flex: 1, minWidth: 125 },
  {
    field: "division",
    headerName: "Division",
    flex: 1,
    minWidth: 95,
    valueFormatter: ({ value }) => value || "—",
  },
  { field: "location", headerName: "Location", flex: 1, minWidth: 110 },
  {
    field: "startDate",
    headerName: "Start Date",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
  { field: "kudosCount", headerName: "Kudos", width: 90, ...centeredCount },
  {
    field: "correctiveCount",
    headerName: "Corrective",
    width: 100,
    ...centeredCount,
  },
  { field: "status", headerName: "Status", width: 100 },
];

const vehicleColumns = [
  {
    field: "assetName",
    headerName: "Plate / Driver",
    flex: 1,
    minWidth: 125,
    cellClassName: "rdGridCell--text",
    renderCell: (params) => (
      <span>
        <span style={{ fontWeight: 600 }}>{params.value || "—"}</span>
        {params.row.assignedDriver && (
          <span className="rdStatCard__subtitle"> ({params.row.assignedDriver})</span>
        )}
      </span>
    ),
  },
  { field: "makeAndModel", headerName: "Make & Model", flex: 1, minWidth: 125, valueFormatter: ({ value }) => value || "—" },
  { field: "division", headerName: "Division", flex: 1, minWidth: 95, valueFormatter: ({ value }) => value || "—" },
  {
    field: "startDate",
    headerName: "In Service",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
  { field: "kudosCount", headerName: "Kudos", width: 90, ...centeredCount },
  { field: "correctiveCount", headerName: "Corrective", width: 100, ...centeredCount },
  { field: "status", headerName: "Status", width: 100 },
];

const equipmentColumns = [
  { field: "assetName", headerName: "Equipment", flex: 1, minWidth: 125, valueFormatter: ({ value }) => value || "—" },
  { field: "serialNumber", headerName: "Serial", flex: 1, minWidth: 110, valueFormatter: ({ value }) => value || "—" },
  { field: "division", headerName: "Division", flex: 1, minWidth: 95, valueFormatter: ({ value }) => value || "—" },
  {
    field: "startDate",
    headerName: "In Service",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
  { field: "kudosCount", headerName: "Kudos", width: 90, ...centeredCount },
  { field: "correctiveCount", headerName: "Corrective", width: 100, ...centeredCount },
  { field: "status", headerName: "Status", width: 100 },
];

// Header strip above each Active/Inactive table — mirrors HTML's .tbl-header:
// title left, "{n} {unit}" right, with a "click for details" hint appended
// only for Active tables, and only once the table actually has rows (empty
// table ⇒ no "click …" hint, per the house rule).
const TableHeaderBar = ({ title, count, unit, hint }) => (
  <div className="rdCard__title rdCard__title--bar">
    <span className="rdCard__titleBar">{title}</span>
    <span className="rdCard__note">
      {count} {unit}
      {hint && count > 0 ? " · click for details" : ""}
    </span>
  </div>
);

const ProjectsTable = ({ list, title, hint, onRowClick }) => (
  <div className="rdCard">
    <div className="rdTableWrap">
      <TableHeaderBar title={title} count={list.totalCount} unit="projects" hint={hint} />
      <DashboardTable
        columns={projectColumns}
        rows={withId(list.rows, "projectId")}
        onRowClick={(params) => onRowClick(params.row)}
        serverMode
        rowCount={list.totalCount}
        page={list.page}
        onPageChange={list.setPage}
        loading={list.loading}
        pageSize={list.pageSize}
      />
    </div>
  </div>
);

const AssetTable = ({ list, columns, title, unit, hint, onRowClick }) => (
  <div className="rdCard">
    <div className="rdTableWrap">
      <TableHeaderBar title={title} count={list.totalCount} unit={unit} hint={hint} />
      <DashboardTable
        columns={columns}
        rows={withId(list.rows, "assetId")}
        onRowClick={(params) => onRowClick(params.row)}
        serverMode
        rowCount={list.totalCount}
        page={list.page}
        onPageChange={list.setPage}
        loading={list.loading}
        pageSize={list.pageSize}
      />
    </div>
  </div>
);

const AssetsSection = ({ data, apiCtx }) => {
  const { kpis } = data;
  const divisionStats = data.divisionStats || [];
  // Registry SIZES only — the page payload no longer carries the vehicle/equipment/custom-asset
  // rows themselves. It used to ship all three registries in full and this section measured them
  // with .length, while every table below rendered from the paged endpoints regardless; the
  // counts now come off kpis so the rows are fetched a page at a time and nowhere else.
  const totalVehicles = kpis.totalVehicles || 0;
  const totalEquipment = kpis.totalEquipment || 0;
  const totalCustomAssets = kpis.totalCustomAssets || 0;
  const totalAssets = kpis.totalProjects + totalVehicles + totalEquipment + totalCustomAssets;
  const [detail, setDetail] = useState(null);
  const [assetDetail, setAssetDetail] = useState(null);
  // Linked cases are fetched in full (bounded — see fetchAllPages) and paged
  // client-side rather than server-side, since the "page" here is a slice of
  // a merged+deduped two-search result, not a single API page. Reset to 0 by
  // openProjectDetail every time a new project's modal opens, so paging into
  // project A's list doesn't leave project B's shorter list stranded on a
  // page past its end.
  const [linkedCasesPage, setLinkedCasesPage] = useState(0);

  // Page-payload-fed tables below have no fetch of their own to report; this
  // gives them a brief loading state when the section mounts (tab switch). A
  // page-payload refetch (date-range change) is deliberately NOT included: the
  // shell shows one centred spinner for that, so a section adding its own here
  // would put a second loader on screen for a single click.
  const mountFlash = useMountFlash();
  const tableLoading = mountFlash;

  const activeList = usePaginatedList(
    (params) => fetchProjects(apiCtx, { ...params, status: "Active" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const inactiveList = usePaginatedList(
    (params) => fetchProjects(apiCtx, { ...params, status: "Inactive" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const activeVehicles = usePaginatedList(
    (params) => fetchVehicles(apiCtx, { ...params, status: "Active" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const inactiveVehicles = usePaginatedList(
    (params) => fetchVehicles(apiCtx, { ...params, status: "Inactive" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const activeEquipment = usePaginatedList(
    (params) => fetchEquipment(apiCtx, { ...params, status: "Active" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const inactiveEquipment = usePaginatedList(
    (params) => fetchEquipment(apiCtx, { ...params, status: "Inactive" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const activeCustomAssets = usePaginatedList(
    (params) => fetchCustomAssets(apiCtx, { ...params, status: "Active" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const inactiveCustomAssets = usePaginatedList(
    (params) => fetchCustomAssets(apiCtx, { ...params, status: "Inactive" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );

  // Best-effort match — no precise Case-to-Project join in the schema (IncCase has only
  // a free-text Location, no ProjectId), so this is an approximation.
  const openProjectDetail = async (p) => {
    const needle = p.projectName || "";
    const locNeedle = p.location || "";
    const searches = [...new Set([needle, locNeedle].filter(Boolean))];
    // Each search is walked to completion (bounded at 1000 rows) rather than
    // a single 100-row page — a project name or location shared by more than
    // 100 cases used to silently lose everything past the first page.
    const results = await Promise.all(
      searches.map((search) =>
        fetchAllPages((params) => fetchCases(apiCtx, params), { search }, { pageSize: 100, maxRows: 1000 }),
      ),
    );
    const seen = new Set();
    const linkedCases = results
      .flatMap((r) => r.rows)
      .filter((c) => (seen.has(c.caseId) ? false : (seen.add(c.caseId), true)));
    // Surfaced as a visible note in the modal (see the "note" section below)
    // rather than left for the viewer to notice the count looks suspiciously
    // round — true whenever either search hit fetchAllPages' own maxRows cap.
    const truncated = results.some((r) => r.truncated);
    setLinkedCasesPage(0); // reset paging — see the state's own comment above
    setDetail({ project: p, linkedCases, truncated });
  };

  const openAssetDetail = (kind, row) => setAssetDetail({ kind, row });

  return (
    <div>
      <StatCardRow
        items={[
          {
            label: "Total Assets",
            value: totalAssets,
            tone: "primary",
            subtitle: "Across all 4 asset types",
          },
          { label: "Projects", value: kpis.totalProjects, tone: "accent" },
          { label: "Vehicles", value: totalVehicles, tone: "action" },
          { label: "Equipment", value: totalEquipment, tone: "kudos" },
        ]}
        columns={4}
      />

      {divisionStats.length > 0 && (
        <>
          <div className="rdSectionLabel rdSectionLabel--first">
            Division Safety Leaderboard
          </div>
          <div className="rdCard">
            <div className="rdCard__title">
              <span>Division Performance Ranking</span>
              <span className="rdCard__note">sorted by corrective count</span>
            </div>
            {tableLoading ? (
              <TableLoading />
            ) : (
              <div className="rdTableWrap">
                <table className="rdTable">
                  <thead>
                    <tr>
                      <th>Division</th>
                      <th className="rdTable__cell--right">Projects</th>
                      <th className="rdTable__cell--right">Kudos</th>
                      <th className="rdTable__cell--right">Corrective</th>
                      <th className="rdTable__cell--right">Corrective Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {divisionStats.slice(0, 12).map((d, i) => (
                      <tr key={d.division}>
                        <td
                          className={i === 0 ? "rdTable__cell--strong" : undefined}
                        >
                          {toPlainText(d.division)}
                        </td>
                        <td className="rdTable__cell--right">{d.projects}</td>
                        <td className="rdTable__cell--right rdTable__cell--value is-kudos">
                          {d.kudos}
                        </td>
                        <td className="rdTable__cell--right rdTable__cell--value is-corrective">
                          {d.corrective}
                        </td>
                        <td
                          className={`rdTable__cell--right rdTable__cell--value is-${rateTone(d.corrRate)}`}
                        >
                          {d.corrRate}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="rdSectionLabel">Projects</div>
      <div className="rdSectionLabel rdSectionLabel--first">Active Projects</div>
      <ProjectsTable
        list={activeList}
        title="Active Projects"
        hint
        onRowClick={openProjectDetail}
      />
      <div className="rdSectionLabel">Inactive Projects</div>
      <ProjectsTable
        list={inactiveList}
        title="Inactive Projects"
        onRowClick={openProjectDetail}
      />

      <div className="rdSectionLabel">Vehicles</div>
      {totalVehicles > 0 ? (
        <>
          <div className="rdSectionLabel rdSectionLabel--first">Active Vehicles</div>
          <AssetTable
            list={activeVehicles}
            columns={vehicleColumns}
            title="Active Vehicles"
            unit="vehicles"
            hint
            onRowClick={(row) => openAssetDetail("vehicle", row)}
          />
          <div className="rdSectionLabel">Inactive Vehicles</div>
          <AssetTable
            list={inactiveVehicles}
            columns={vehicleColumns}
            title="Inactive Vehicles"
            unit="vehicles"
            onRowClick={(row) => openAssetDetail("vehicle", row)}
          />
        </>
      ) : (
        <div className="rdCard">
          <div className="rdEmpty rdEmpty--center">
            No vehicles tracked for this client.
          </div>
        </div>
      )}

      <div className="rdSectionLabel">Equipment</div>
      {totalEquipment > 0 ? (
        <>
          <div className="rdSectionLabel rdSectionLabel--first">Active Equipment</div>
          <AssetTable
            list={activeEquipment}
            columns={equipmentColumns}
            title="Active Equipment"
            unit="items"
            hint
            onRowClick={(row) => openAssetDetail("equipment", row)}
          />
          <div className="rdSectionLabel">Inactive Equipment</div>
          <AssetTable
            list={inactiveEquipment}
            columns={equipmentColumns}
            title="Inactive Equipment"
            unit="items"
            onRowClick={(row) => openAssetDetail("equipment", row)}
          />
        </>
      ) : (
        <div className="rdCard">
          <div className="rdEmpty rdEmpty--center">
            No equipment tracked for this client.
          </div>
        </div>
      )}

      <div className="rdSectionLabel">Custom Assets</div>
      {totalCustomAssets > 0 ? (
        // Unlike Vehicles/Equipment (fabricated registries), Custom Assets has
        // a real backend registry that the v26 HTML mock never populated —
        // the mock treated it as permanently empty. When this client actually
        // has Custom Asset records, showing the real Active/Inactive tables
        // (instead of collapsing to the "not configured" note) is intentional:
        // omitting real data here would show the client less than the truth.
        <>
          <div className="rdSectionLabel rdSectionLabel--first">Active Custom Assets</div>
          <AssetTable
            list={activeCustomAssets}
            columns={equipmentColumns}
            title="Active Custom Assets"
            unit="items"
            hint
            onRowClick={(row) => openAssetDetail("customAsset", row)}
          />
          <div className="rdSectionLabel">Inactive Custom Assets</div>
          <AssetTable
            list={inactiveCustomAssets}
            columns={equipmentColumns}
            title="Inactive Custom Assets"
            unit="items"
            onRowClick={(row) => openAssetDetail("customAsset", row)}
          />
        </>
      ) : (
        <div className="rdCard">
          <div className="rdEmpty rdEmpty--center">
            No Custom Assets currently configured for this client — this asset
            type has no recorded activity in the data on file.
          </div>
        </div>
      )}

      {detail && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={detail.project.projectName}
          fields={[
            { label: "Status", value: detail.project.status },
            { label: "Division", value: detail.project.division || "—" },
            { label: "Project ID", value: detail.project.projectId },
            {
              label: "Start Date",
              value: formatDate(detail.project.startDate),
            },
            { label: "Location", value: detail.project.location },
          ]}
          sections={[
            {
              title: "Safety Activity",
              type: "statRow",
              columns: 3,
              items: [
                { label: "Kudos", value: detail.project.kudosCount || 0, tone: "kudos" },
                { label: "Corrective", value: detail.project.correctiveCount || 0, tone: "corrective" },
                {
                  // Labelled "Kudos + Corrective", not "Total Items" -- this asset
                  // registry only ever tracks these two ItemTypes (no Action/Reprimand
                  // counts exist for projects), so calling the sum a "Total" implied a
                  // completeness the underlying data doesn't have. See PLAN-open-items
                  // -basis.md WS5.3 / deferred item L3.
                  label: "Kudos + Corrective",
                  value: (detail.project.kudosCount || 0) + (detail.project.correctiveCount || 0),
                  tone: "accent",
                },
              ],
            },
            // Only rendered when fetchAllPages actually hit its maxRows bound on
            // one of the two underlying searches — see the comment on
            // `truncated` in openProjectDetail above. Never silently truncate:
            // a bounded match list has to say so.
            ...(detail.truncated
              ? [{
                  type: "note",
                  title: "Note",
                  text: "This list may be incomplete — one of the underlying searches (by project name or by location) matched more than 1,000 cases and was capped at that limit.",
                }]
              : []),
            {
              title: "Linked Cases (best-effort location match)",
              type: "cards",
              emptyLabel: "No linked cases found",
              // Server-paged lists elsewhere hand DashboardTable/RecordModal
              // a single already-fetched page; this list is the opposite —
              // fetchAllPages above already pulled every matching row (up to
              // the bound), so pagination here is a plain client-side slice
              // of the merged+deduped array instead of a new request per page.
              rows: detail.linkedCases
                .slice(
                  linkedCasesPage * LINKED_CASES_PAGE_SIZE,
                  (linkedCasesPage + 1) * LINKED_CASES_PAGE_SIZE,
                )
                .map((c) => ({
                  // HTML shows just the status pill + date on the card's top
                  // line (no leading label) — title is left blank so the pill
                  // and date render in the cards section's meta slot.
                  title: "",
                  pill: c.status,
                  meta: formatDate(c.dateOfEvent) || "—",
                  // CaseDto has no reporter field distinct from Location (that's
                  // an IncidentsSection-only aggregate), so the HTML's
                  // Location||EnteredBy fallback drops the EnteredBy leg here.
                  note: c.location || `Case #${c.caseId}`,
                })),
              rowCount: detail.linkedCases.length,
              page: linkedCasesPage,
              pageSize: LINKED_CASES_PAGE_SIZE,
              onPageChange: setLinkedCasesPage,
              loading: false,
            },
          ]}
        />
      )}

      {assetDetail && (
        <RecordModal
          open
          onClose={() => setAssetDetail(null)}
          title={assetDetail.row.assetName || "Asset"}
          fields={
            assetDetail.kind === "vehicle"
              ? [
                  { label: "Status", value: assetDetail.row.status },
                  { label: "Division", value: assetDetail.row.division || "—" },
                  { label: "Vehicle ID", value: assetDetail.row.assetId },
                  { label: "Year", value: assetDetail.row.year || "—" },
                  { label: "License Plate", value: assetDetail.row.licensePlate || "—" },
                  { label: "In Service", value: formatDate(assetDetail.row.startDate) },
                  { label: "Assigned Driver", value: assetDetail.row.assignedDriver || "—" },
                ]
              : [
                  { label: "Status", value: assetDetail.row.status },
                  { label: "Division", value: assetDetail.row.division || "—" },
                  {
                    label: assetDetail.kind === "equipment" ? "Equipment ID" : "Asset ID",
                    value: assetDetail.row.assetId,
                  },
                  { label: "Serial Number", value: assetDetail.row.serialNumber || "—" },
                  { label: "In Service", value: formatDate(assetDetail.row.startDate) },
                ]
          }
          sections={[
            {
              title: "Safety Activity",
              type: "statRow",
              columns: 3,
              items: [
                { label: "Kudos", value: assetDetail.row.kudosCount || 0, tone: "kudos" },
                { label: "Corrective", value: assetDetail.row.correctiveCount || 0, tone: "corrective" },
                {
                  // Same "Kudos + Corrective" relabel as the project detail modal above
                  // -- vehicle/equipment/custom-asset registries also only track these
                  // two ItemTypes, so "Total Items" overclaimed completeness.
                  label: "Kudos + Corrective",
                  value: (assetDetail.row.kudosCount || 0) + (assetDetail.row.correctiveCount || 0),
                  tone: "accent",
                },
              ],
            },
          ]}
        />
      )}
    </div>
  );
};

export default memo(AssetsSection);
